/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-14
 */

// Add third-party dependencies.
import { Routes, Route } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';

import Home from '../components/pages/Home';
import StudioDashboard from '../components/pages/StudioDashboard/StudioDashboard';
import InvitationsPage from '../components/pages/StudioDashboard/InvitationsPage';
import JoinRequestsPage from '../components/pages/StudioDashboard/JoinRequestsPage';
import MembersPage from '../components/pages/StudioDashboard/MembersPage';
import MemberDetailPage from '../components/studios/members/MemberDetailPage';
import SettingsPage from '../components/pages/StudioDashboard/SettingsPage';
import { Dashboard } from '../components/pages/Dashboard';
import Studios from '../components/pages/Studios';
import { StudioCreate } from '../components/pages/StudioCreate';
import { StudioEdit } from '../components/pages/StudioEdit';
import { StudioTheme } from '../components/pages/StudioTheme';
import InvitationDetailPage from '../components/pages/InvitationDetail';
// Waitlist imports commented out for MVP launch - keep for potential rollback
// import Waitlist from '../components/pages/Waitlist';
// import WaitlistSuccess from '../components/pages/Waitlist/WaitlistSuccess';
// import WaitlistVerification from '../components/pages/Waitlist/WaitlistVerification';
// import WaitlistPosition from '../components/pages/Waitlist/WaitlistPosition';
import Contact from '../components/pages/Contact';
import GettingStarted from '../components/pages/GettingStarted';
import Register from '../components/pages/Register';
import Login from '../components/pages/Login';
import Terms from '../components/pages/Terms';
import Privacy from '../components/pages/Privacy';
import EmailVerification from '../components/pages/EmailVerification';
import EmailVerificationConfirm from '../components/pages/EmailVerification/EmailVerificationConfirm';
import PasswordReset from '../components/pages/PasswordReset';
import PasswordResetConfirm from '../components/pages/PasswordReset/PasswordResetConfirm';
import Profile from '../components/pages/Profile';
import Settings from '../components/pages/Settings';
import { Tools } from '../components/pages/Tools';
import BillingPage from '../components/pages/Billing/BillingPage';
import PricingPage from '../components/pages/Pricing/PricingPage';
import AddStudio from '../components/pages/AddStudio';
import Changelog from '../components/pages/Changelog/Changelog';
import {
  TeamBiosManagement,
  TeamBiosEditMembers,
  PublicTeamBiosPage,
} from '../features/teamBios';
import StudentsManagement from '../features/students/StudentsManagement';
import {
  BirthdayWidgetManagement,
  PublicBirthdayWidgetPage,
} from '../features/birthdayWidget';
import {
  AnnouncementsManagement,
  PublicAnnouncementsPage,
} from '../features/announcements';
import { QuickBooksManagement } from '../features/quickbooks';
import { FinancialInsightsDashboard } from '../features/financialInsights';
import SchedulingManagement from '../features/scheduling/SchedulingManagement';
import ScheduleDetail from '../features/scheduling/ScheduleDetail';
import PublicSchedulePage from '../features/scheduling/PublicSchedulePage';
import { ShoppingListPage } from '../features/shoppingList';
import ClassEssentialsPage from '../features/classEssentials/ClassEssentialsPage';
import ParentChecklistView from '../features/classEssentials/ParentChecklistView';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import NotFound from './NotFound';
import { useSessionAuth } from '../hooks/useSessionAuth';

/**
 * Application routes component.
 * @example
 * return (
 *   <AppRoutes />
 * )
 * @returns {React.ReactElement} component.
 */
const AppRoutes: React.FC = () => {
  // Initialize session authentication on app startup
  const {
    isLoading: isCheckingSession,
    isInitialized,
    sessionCheckAttempts,
  } = useSessionAuth();

  // Show loading spinner while initializing session
  if (!isInitialized || isCheckingSession) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={50} />
        <Typography variant="h6" color="text.secondary">
          Loading...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {sessionCheckAttempts > 0
            ? `Verifying session (attempt ${sessionCheckAttempts}/3)...`
            : 'Checking authentication status...'}
        </Typography>
      </Box>
    );
  }

  // Render application routes once session initialization is complete
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route
        path="/studio/:studioId"
        element={
          <ProtectedRoute>
            <StudioDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/studios/:studioId/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/studio/:studioId/edit"
        element={
          <ProtectedRoute>
            <StudioEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/studio/:studioId/invitations"
        element={
          <ProtectedRoute>
            <InvitationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/studio/:studioId/members"
        element={
          <ProtectedRoute>
            <MembersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/studio/:studioId/members/:memberId"
        element={
          <ProtectedRoute>
            <MemberDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/studio/:studioId/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/studio/:studioId/join-requests"
        element={
          <ProtectedRoute>
            <JoinRequestsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/theme"
        element={
          <ProtectedRoute>
            <StudioTheme />
          </ProtectedRoute>
        }
      />
      <Route
        path="/studios"
        element={
          <ProtectedRoute>
            <Studios />
          </ProtectedRoute>
        }
      />
      <Route
        path="/studios/create"
        element={
          <ProtectedRoute>
            <StudioCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/studios/add"
        element={
          <ProtectedRoute>
            <AddStudio />
          </ProtectedRoute>
        }
      />
      <Route path="/invite/:token" element={<InvitationDetailPage />} />
      {/* Waitlist routes commented out for MVP launch - keep for potential rollback */}
      {/* <Route path="/waitlist" element={<Waitlist />} /> */}
      {/* <Route path="/waitlist/success" element={<WaitlistSuccess />} /> */}
      {/* <Route
        path="/waitlist/verify/:token"
        element={<WaitlistVerification />}
      /> */}
      {/* <Route path="/waitlist/position" element={<WaitlistPosition />} /> */}
      <Route path="/contact" element={<Contact />} />
      <Route path="/getting-started" element={<GettingStarted />} />
      <Route path="/register" element={<Register />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<PasswordReset />} />
      <Route path="/reset-password" element={<PasswordResetConfirm />} />
      <Route path="/verify-email" element={<EmailVerification />} />
      <Route
        path="/verify-email/:token"
        element={<EmailVerificationConfirm />}
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools"
        element={
          <ProtectedRoute>
            <Tools />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-bios"
        element={
          <ProtectedRoute>
            <TeamBiosManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-bios/edit/:studioId"
        element={
          <ProtectedRoute>
            <TeamBiosEditMembers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/public/team-bios/:studioSlug"
        element={<PublicTeamBiosPage />}
      />
      <Route
        path="/public/birthday-widget/:studioSlug"
        element={<PublicBirthdayWidgetPage />}
      />
      <Route
        path="/public/announcements/:studioSlug"
        element={<PublicAnnouncementsPage />}
      />
      <Route
        path="/schedule/:scheduleIdOrSlug"
        element={<PublicSchedulePage />}
      />
      <Route
        path="/students"
        element={
          <ProtectedRoute>
            <StudentsManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/happy-birthday-widget"
        element={
          <ProtectedRoute>
            <BirthdayWidgetManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/announcements"
        element={
          <ProtectedRoute>
            <AnnouncementsManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quickbooks"
        element={
          <ProtectedRoute>
            <QuickBooksManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/financial-insights"
        element={
          <ProtectedRoute>
            <FinancialInsightsDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/studios/:studioId/financial-insights"
        element={
          <ProtectedRoute>
            <FinancialInsightsDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/scheduling"
        element={
          <ProtectedRoute>
            <SchedulingManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/scheduling/:id"
        element={
          <ProtectedRoute>
            <ScheduleDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shopping-list"
        element={
          <ProtectedRoute>
            <ShoppingListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/class-essentials"
        element={
          <ProtectedRoute>
            <ClassEssentialsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/class-essentials/view/:token"
        element={<ParentChecklistView />}
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/changelog" element={<Changelog />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

// Export component.
export default AppRoutes;
